<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import { showApiError } from '$lib/util/errors'

  type Lead =
    paths['/provider-leads']['get']['responses'][200]['content']['application/json']['items'][number]

  let items = $state<Lead[]>([])
  let loading = $state(false)

  async function load(): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/provider-leads', {})
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = data?.items ?? []
    loading = false
  }

  async function markContacted(id: number): Promise<void> {
    const { data, error } = await api.PATCH('/provider-leads/{id}', {
      params: { path: { id } },
      body: { status: 'CONTACTED' },
    })
    if (error || !data) {
      showApiError(error)
      return
    }
    items = items.map((l) => (l.id === id ? data : l))
  }

  onMount(() => {
    void load()
  })
</script>

<div class="section-header">
  <h1>Provider leads</h1>
</div>

{#if items.length === 0}
  <p class="muted">{loading ? 'Loading…' : 'No leads yet.'}</p>
{:else}
  <table>
    <thead>
      <tr>
        <th>Company</th>
        <th>Name</th>
        <th>Email</th>
        <th>Phone</th>
        <th>Portfolio</th>
        <th>Message</th>
        <th>Status</th>
        <th>Received</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      {#each items as l (l.id)}
        <tr>
          <td>{l.company}</td>
          <td>{l.name}</td>
          <td>{l.email}</td>
          <td>{l.phone ?? '-'}</td>
          <td>{l.portfolioSize ?? '-'}</td>
          <td>{l.message ?? '-'}</td>
          <td>{l.status}</td>
          <td>{new Date(l.createdAt).toLocaleDateString()}</td>
          <td>
            {#if l.status === 'NEW'}
              <button type="button" class="primary" onclick={() => void markContacted(l.id)}>
                Mark contacted
              </button>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .muted {
    color: var(--muted);
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
</style>
